import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useWebSocket } from "../context/WebSocketContext";
import Message from "./Message";
import { Body, Warning } from "./styles/ChatStyles";
import { useClientConfig } from "../hooks/useClientConfig";
import { debugLog } from "../utils/debug";
import EndSessionModal from "./EndSessionModal";

const ChatBody = ({ messages }) => {
  const {
    chatEnded,
    setMessages,
    showEndModal,
    setShowEndModal,
    sendPayload,
  } = useWebSocket();

  const bottomRef = useRef(null);
  const [historicoCarregado] = useState(false);
  const config = useClientConfig();
  const showHistory = String(config?.showHistory).toLowerCase() === "true";

  useEffect(() => {
    debugLog("🔽 Rolando até a última mensagem...");
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleBuscarHistorico = () => {
    const sessionHistories = JSON.parse(localStorage.getItem("sessionHistories") || "[]");

    if (!sessionHistories.length) {
      toast.info("📁 Nenhuma sessão anterior encontrada.");
      debugLog("❌ Nenhuma sessão anterior encontrada no localStorage.");
      return;
    }

    const ultimaSessao = sessionHistories.at(-1);

    if (!ultimaSessao?.messages?.length) {
      toast.info("📁 Última sessão está vazia.");
      debugLog("🟡 Última sessão está vazia.");
      return;
    }

    const textosExistentes = new Set(messages.map((m) => `${m.text}-${m.timestamp}`));
    const novos = ultimaSessao.messages.filter(
      (msg) => !textosExistentes.has(`${msg.text}-${msg.timestamp}`)
    );

    if (!novos.length) {
      toast.info("🟡 Mensagens da última sessão já estão visíveis.");
      debugLog("⚠️ Nenhuma nova mensagem da última sessão para adicionar.");
      return;
    }

    setMessages((prev) => [...prev, ...novos]);
    toast.success("✅ Última sessão adicionada ao histórico.");
    debugLog(`📥 Adicionadas ${novos.length} mensagens da última sessão ao chat.`);
  };

  const getSenderKey = (msg) => {
    const source = (msg.sourceType || "").toUpperCase();
    const from = (msg.from || "").toLowerCase();
    const nickname = (msg.senderNickname || "").toLowerCase();

    if (
      source === "SYSTEM" ||
      source === "ROBOT" ||
      from === "system" ||
      from === "bot" ||
      nickname === "sofi" ||
      nickname === "sistema"
    ) {
      return "sofi";
    }

    if (source === "AGENT") return "agent:" + (msg.senderNickname || "agente");
    if (from === "user") return "user";

    return "outro";
  };

  const shouldGroupMessages = (current, previous) => {
    if (!previous) return false;

    const sameSenderKey = getSenderKey(current) === getSenderKey(previous);
    const currentTime = new Date(current.timestamp || Date.now());
    const previousTime = new Date(previous.timestamp || Date.now());
    const withinFiveMinutes = Math.abs(currentTime - previousTime) < 5 * 60 * 1000;

    return sameSenderKey && withinFiveMinutes;
  };

  const emojiOptions = [
    { emoji: "😍", label: "5 - Ótimo", value: "5" },
    { emoji: "😃", label: "4 - Gostei", value: "4" },
    { emoji: "😐", label: "3 - Regular", value: "3" },
    { emoji: "😟", label: "2 - Achei ruim", value: "2" },
    { emoji: "😠", label: "1 - Péssimo", value: "1" },
  ];

  const handleDownload = () => {
    try {
      const text = messages.map((m) => `[${m.time || ""}] ${m.from || ""}: ${m.text || ""}`).join("\n");
      const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `chat-historico-${new Date().toISOString().split("T")[0]}.txt`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("✅ Histórico baixado.");
    } catch (e) {
      console.error("Erro ao baixar histórico:", e);
      toast.error("❌ Erro ao baixar histórico.");
    }
  };

  return (
    <Body>
      {showHistory && (
        <button
          onClick={handleBuscarHistorico}
          disabled={historicoCarregado}
          style={{
            margin: "8px auto",
            padding: "6px 12px",
            fontSize: "14px",
            borderRadius: "6px",
            border: "1px solid #ccc",
            background: historicoCarregado ? "#e0e0e0" : "#f4f4f4",
            color: historicoCarregado ? "#999" : "#000",
            cursor: historicoCarregado ? "not-allowed" : "pointer",
            display: "block",
          }}
        >
          🔍 {historicoCarregado ? "Histórico já carregado" : "Buscar Histórico"}
        </button>
      )}

      {chatEnded && (
        <Warning>
          Prezado cliente, não foi possível realizar o nosso atendimento através deste canal. Por gentileza, entre em contato através do número <strong>PENDENTE</strong>. Obrigado.
        </Warning>
      )}

      {messages.map((msg, index) => {
        const previous = messages[index - 1];
        const grouped = shouldGroupMessages(msg, previous);
        debugLog("🧩 Renderizando mensagem:", {
          text: msg.text,
          from: msg.from,
          grouped,
        });
        const key = `${msg.timestamp || index}-${index}`;
        return (
          <Message
            key={key}
            {...msg}
            grouped={grouped}
            type={msg.mediaType}
            senderAvatar={msg.senderAvatar}
          />
        );
      })}
      <div ref={bottomRef} />

      <EndSessionModal
        show={showEndModal}
        onClose={() => setShowEndModal(false)}
        onDownload={handleDownload}
        onSubmit={(type, payload) => {
          try {
            sendPayload(type, payload);
          } catch (e) {
            console.error("Erro ao enviar payload de avaliação:", e);
            toast.error("❌ Erro ao enviar avaliação.");
          }
          const emoji = emojiOptions.find(opt => opt.value === payload.evaluation)?.emoji || "⭐";
          setMessages((prev) => [
            ...prev,
            {
              from: "system",
              text: `📄 Avaliação registrada: ${emoji}`,
              time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
              timestamp: Date.now(),
            },
          ]);
          setShowEndModal(false);
        }}
      />
    </Body>
  );
};

export default ChatBody;
